import type {
    AggregationSearchableListResult,
    AwardsItem,
} from '@modules-rocco/search/types/aggregations';
import {
    createSearchableListFilterState,
    type SearchableListFilterState,
} from '@modules-rocco/filters/filter-state/searchable-list-filter-state';
import type { SingleValueFilterState } from '@modules-rocco/filters/filter-state/single-value-filter-state';

/**
 * URL sentinel meaning "every award selected"; it expands to the full list at
 * state-build time, so subsequent writes emit the explicit values.
 */
export const AWARDS_ALL_SENTINEL = 'all';

/**
 * An award entry in the awards list state. The state itself is a flat
 * searchable list keyed by award value; the institution fields exist purely
 * so the UI can group awards under their institution.
 */
export interface AwardsListItem extends SingleValueFilterState {
    institutionValue?: string;
    institutionLabel?: string;
}

/** The aggregate response has carried both shapes; accept either label key. */
type AwardsAggregationItem = Partial<AwardsItem> & { label?: string };

/**
 * Apply a toggle to an awards selection, keeping the "All" sentinel honest:
 * toggling the All row selects or clears everything, unchecking any real
 * award drops the sentinel (so the uncheck survives the URL round-trip), and
 * completing the set by hand re-earns it.
 */
export const applyAwardsSelection = (
    current: ReadonlySet<string>,
    allValues: readonly string[],
    toggled: readonly string[],
    checked: boolean,
): Set<string> => {
    const realValues = allValues.filter(
        (value) => value !== AWARDS_ALL_SENTINEL,
    );
    const hasSentinel = allValues.includes(AWARDS_ALL_SENTINEL);
    const next = new Set(current);

    if (toggled.includes(AWARDS_ALL_SENTINEL)) {
        next.clear();
        if (checked) {
            realValues.forEach((value) => next.add(value));
            if (hasSentinel) {
                next.add(AWARDS_ALL_SENTINEL);
            }
        }
        return next;
    }

    for (const value of toggled) {
        if (checked) {
            next.add(value);
        } else {
            next.delete(value);
        }
    }

    if (!checked) {
        next.delete(AWARDS_ALL_SENTINEL);
    } else if (hasSentinel && realValues.every((value) => next.has(value))) {
        next.add(AWARDS_ALL_SENTINEL);
    }

    return next;
};

const getAwardLabel = (item: AwardsAggregationItem): string | null =>
    item.name ?? item.label ?? item.value ?? null;

/**
 * Create the awards filter state from the search aggregations and the
 * selection restored from the URL.
 */
export const createAwardsFilterStateFromAggregationResult = (
    aggregationResult?: AggregationSearchableListResult<AwardsItem>,
    urlSelectedItems?: SingleValueFilterState[],
): SearchableListFilterState => {
    const uniqueItems = (aggregationResult?.items ?? [])
        .filter((item): item is AwardsItem => !!item.value)
        .filter(
            (item, index, self) =>
                self.findIndex((other) => other.value === item.value) === index,
        );
    const realItems = uniqueItems.filter(
        (item) => item.value !== AWARDS_ALL_SENTINEL,
    );
    // Legacy rule: the "All" pseudo-option only earns a row when there are
    // at least two real awards to select across.
    const sourceItems = realItems.length <= 1 ? realItems : uniqueItems;

    const items: AwardsListItem[] = sourceItems.map(
        (item: AwardsAggregationItem) => ({
            value: item.value ?? '',
            label: getAwardLabel(item),
            count: null,
            checked: false,
            disabled: false,
            institutionValue: item.institutionValue,
            institutionLabel: item.institutionName,
        }),
    );

    const selectAll = urlSelectedItems?.some(
        (item) => item.value === AWARDS_ALL_SENTINEL,
    );
    const selectedValues = new Set(
        (urlSelectedItems ?? []).map((item) => item.value),
    );

    const knownValues = new Set(items.map((item) => item.value));
    // Selections restored from the URL survive even when the current
    // aggregation buckets no longer include them, matching the shared
    // searchable-list factory's union semantics.
    const orphanSelections: AwardsListItem[] = selectAll
        ? []
        : (urlSelectedItems ?? [])
              .filter(
                  (item) =>
                      item.value !== AWARDS_ALL_SENTINEL &&
                      !knownValues.has(item.value),
              )
              .map((item) => ({
                  value: item.value,
                  label: item.label ?? item.value,
                  count: null,
                  checked: true,
                  disabled: false,
              }));

    const selectedItems = selectAll
        ? items.map((item) => ({ ...item, checked: true }))
        : [
              ...items
                  .filter((item) => selectedValues.has(item.value))
                  .map((item) => ({ ...item, checked: true })),
              ...orphanSelections,
          ];

    const selectedByValue = new Set(selectedItems.map((item) => item.value));

    return createSearchableListFilterState({
        items: items.map((item) =>
            selectedByValue.has(item.value) ? { ...item, checked: true } : item,
        ),
        selectedItems,
        search: '',
        allowMultipleSelectedItems: true,
        firstCharacterGroups: {},
    });
};
